import React from "react";
import { Button, Form, Input, Typography } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useForgotPasswordContext } from "context/ForgotPasswordEnumeratorContext";
import { RoleTypes } from "enum/role-type-enum";
import { useEnumeratorPasswordSetup } from "api/enumerator/ChangePassword/usePasswordSetup";
import { useRecruiterPasswordSetup } from "api/recruiter/ChangePassword/usePasswordSetup";

const { Text, Title } = Typography;

interface PasswordSetupProps {
  code: string;
  email: string;
  roleType: string | null;
}

const PasswordSetup: React.FC<PasswordSetupProps> = ({ code, email, roleType }) => {
  const [form] = Form.useForm();

  const enumeratorpasswordSetupMutation = useEnumeratorPasswordSetup();
  const recruiterPasswordSetupMutation = useRecruiterPasswordSetup();

  const { setIsCodeVerified } = useForgotPasswordContext();

  const navigate = useNavigate();

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const parameters = {
      emailOrPhone: email,
      otp: code,
      password: values.password,
      odlPassword: values.oldPassword,
    };

    if (roleType === RoleTypes.ENUMERATOR) {
      enumeratorpasswordSetupMutation.mutate(parameters, {
        onSuccess: () => {
          setIsCodeVerified(false);
          navigate(`/login?roleType=${roleType}`);
        },
      });
    }

    if (roleType === RoleTypes.RECRUITER) {
      recruiterPasswordSetupMutation.mutate(parameters, {
        onSuccess: () => {
          setIsCodeVerified(false);
          navigate(`/login?roleType=${roleType}`);
        },
      });
    }
  };

  return (
    <>
      <Title level={2}>Password Setup</Title>
      <Text type="secondary">Choose a strong password to protect your account.</Text>
      <div className="mt-12">
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item
            name="oldPassword"
            label="Old Password"
            rules={[
              {
                required: true,
                message: "Old Password is required",
              },
              {
                pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{6,}$/,
                message: "Please enter valid old password!",
              },
            ]}
          >
            <Input type="password" placeholder="Enter your old password" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              {
                required: true,
                message: "Password is required",
              },
              {
                pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{6,}$/,
                message: "Please enter valid password!",
              },
            ]}
          >
            <Input type="password" placeholder="Enter your password" />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="oldPassword"
            rules={[
              { required: true, message: "Please confirm password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject("Passwords do not match!");
                },
              }),
            ]}
          >
            <Input type="password" placeholder="Enter your password" />
          </Form.Item>
          <Button
            htmlType="submit"
            type="primary"
            className="h-12 px-8 border border-2 border-border text-base  flex justify-center items-center gap-2 bg-primary text-white border-none w-full mt-12"
            loading={
              roleType === RoleTypes.ENUMERATOR
                ? enumeratorpasswordSetupMutation.isPending
                : recruiterPasswordSetupMutation.isPending
            }
          >
            Save password
          </Button>
        </Form>
      </div>
      <div className="mt-5">
        <Text className="text-base">
          Remember your password? <Link to={`/login?roleType=${roleType}`}>Click here.</Link>
        </Text>
      </div>
    </>
  );
};
export default PasswordSetup;
